import { observer } from 'mobx-react-lite';
import FeedbackContainer from './FeedbackContainer';
import FeedbackContainerOld from './FeedbackContainerOld';
import RelatedContentSender from './RelatedContentSender';
import { Icon } from '@fluentui/react/lib/Icon';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { ReadingPaneEntrySourceType } from 'owa-calendar-forms-common/lib/schema/ReadingPaneEntrySourceType';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { EmailInsightType, InsightsDataFetchSource } from 'owa-meeting-insights-types-and-flights';
import { getInsightsTraceId, lazyOpenEmailInsight } from 'owa-meeting-insights';
import { sentAtLabelSentenceCase } from 'owa-meeting-insights/lib/components/InsightDocRow.locstring.json';
import { getEmailInsightAriaLabel } from 'owa-meeting-insights/lib/helpers/ariaLabelHelpers';
import { getIsOrganizerFromMeetingId } from 'owa-meeting-insights/lib/selectors/instrumentationSelectors';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import * as React from 'react';
import { logEmailFeedbackButtonClicked } from '../helpers/instrumentationHelpers';

const styles = require('./RelatedEmail.scss');
const classNames = require('classnames/bind').bind(styles);

export interface RelatedEmailProps {
    meetingId: string;
    email: EmailInsightType;
    colorScheme: ColorScheme;
    color: string;
    readingPaneEntrySource?: ReadingPaneEntrySourceType; // null when rendering from CITI card
    insightsEntrySource: InsightsDataFetchSource;
    position: number;
    renderedEmailsCount: number;
}

export default observer(function RelatedEmail(props: RelatedEmailProps) {
    const renderFirstRow = (): JSX.Element => {
        const { senderName, senderEmailAddress, sentTimestamp } = props.email;
        const timestampToDisplay = format(loc(sentAtLabelSentenceCase), sentTimestamp);
        return (
            <div className={styles.firstRow}>
                <div className={styles.firstColumn}>
                    {senderName != '' ? (
                        <RelatedContentSender
                            emailAddress={senderEmailAddress}
                            name={senderName}
                            size={PersonaSize.size24}
                            personaType={'NotResolved'}
                        />
                    ) : null}
                </div>
                <div className={styles.secondColumn}>
                    <span className={styles.senderName} title={senderName || senderEmailAddress}>
                        {senderName || senderEmailAddress}
                    </span>
                    <span className={styles.timestamp} title={timestampToDisplay}>
                        {timestampToDisplay}
                    </span>
                </div>
            </div>
        );
    };
    const renderSecondRow = (): JSX.Element => {
        return (
            <div className={styles.secondRow}>
                <span
                    className={classNames(styles.subject, !props.email.isRead && styles.unread)}
                    title={props.email.subject}>
                    {props.email.subject}
                </span>
            </div>
        );
    };
    const renderThirdRow = (): JSX.Element => {
        return (
            <div className={styles.thirdRow}>
                <span className={styles.preview}>{props.email.preview}</span>
                {getEmailInfoIcons()}
            </div>
        );
    };
    const renderReasonMarkerRow = (): JSX.Element => {
        const senderName = props.email.senderName;
        const reasonMarker = props.email.reasonMarker
            ? props.email.reasonMarker
            : 'Shared by ' + senderName + '.';

        // VSO 109482: Update code to handle new API with parameters and reasonCodes to generate localized strings;
        return (
            <div className={styles.reasonMarkerRow}>
                <span className={styles.reasonMarker}>{reasonMarker}</span>
            </div>
        );
    };
    const getEmailInfoIcons = (): JSX.Element => {
        const { hasAttachments, importance } = props.email;
        const attachment = hasAttachments ? (
            <Icon className={styles.attach} iconName={ControlIcons.Attach} />
        ) : null;
        let importanceIcon = null;
        switch (importance) {
            case 'High':
                importanceIcon = (
                    <Icon className={styles.importance} iconName={ControlIcons.Important} />
                );
                break;
            case 'Low':
                importanceIcon = <Icon iconName={ControlIcons.Down} />;
                break;
        }
        return (
            <span className={styles.iconContainer}>
                {attachment} {importanceIcon}
            </span>
        );
    };

    const relatedEmailContainerRef = React.useRef<HTMLDivElement>();

    const onUsefulButtonClicked = () => {
        onEmailFeedbackButtonClicked(true /* isUseful */);
    };
    const onNotUsefulButtonClicked = () => {
        onEmailFeedbackButtonClicked(false /* isUseful */);
    };
    const onEmailFeedbackButtonClicked = (isUseful: boolean) => {
        relatedEmailContainerRef.current?.focus();

        const { referenceId, relevanceRelationship } = props.email;
        const traceId = getInsightsTraceId(props.meetingId, props.insightsEntrySource);
        const isOrganizer = getIsOrganizerFromMeetingId(props.meetingId);
        logEmailFeedbackButtonClicked(
            props.meetingId,
            isUseful,
            traceId,
            referenceId,
            isOrganizer,
            relevanceRelationship,
            props.insightsEntrySource
        );
    };

    const onRelatedEmailClicked = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        const { meetingId, email, readingPaneEntrySource, insightsEntrySource } = props;
        lazyOpenEmailInsight.importAndExecute(
            meetingId,
            email,
            readingPaneEntrySource,
            insightsEntrySource
        );
    };
    const onKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (evt.which === KeyCodes.enter) {
            const { meetingId, email, readingPaneEntrySource, insightsEntrySource } = props;
            lazyOpenEmailInsight.importAndExecute(
                meetingId,
                email,
                readingPaneEntrySource,
                insightsEntrySource
            );
        }
    };

    const { referenceId, relevanceRelationship } = props.email;
    const isOrganizer = getIsOrganizerFromMeetingId(props.meetingId);
    const traceId = getInsightsTraceId(props.meetingId, props.insightsEntrySource);
    if (!traceId) {
        trace.warn('RelatedEmail: invalid traceId');
    }
    const readStatusStyle: React.CSSProperties = props.email.isRead
        ? null
        : {
              position: 'relative',
              borderLeft: '4px solid ' + props.color,
              boxSizing: 'border-box',
          };
    return (
        <div
            ref={relatedEmailContainerRef}
            className={styles.relatedEmailContainer}
            role="listitem"
            tabIndex={0}
            data-is-focusable="true" // needed for FocusZone management to work correctly
            aria-label={getEmailInsightAriaLabel(props.email, true /* includePreview */)}
            aria-posinset={props.position + 1}
            aria-setsize={props.renderedEmailsCount}>
            <div className={styles.relatedEmail}>
                <div
                    className={styles.relatedEmailContent}
                    style={readStatusStyle}
                    onClick={onRelatedEmailClicked}
                    onKeyDown={onKeyDown}>
                    {isFeatureEnabled('cal-meeting-insights-reasonMarkers')
                        ? renderReasonMarkerRow()
                        : renderFirstRow()}
                    {renderSecondRow()}
                    {renderThirdRow()}
                </div>
                <div
                    className={classNames(
                        styles.feedbackHoverTarget,
                        !props.email.isRead && styles.unread
                    )}
                    onClick={onFeedbackContainerClicked}>
                    {isFeatureEnabled('cal-meeting-insights-feedbackUI') ? (
                        <FeedbackContainer
                            colorScheme={props.colorScheme}
                            traceId={traceId}
                            isFileFeedback={false}
                            isOrganizer={isOrganizer}
                            relevanceRelationship={relevanceRelationship}
                            referenceId={referenceId}
                            entrySource={props.insightsEntrySource}
                            meetingId={props.meetingId}
                        />
                    ) : (
                        <FeedbackContainerOld
                            colorScheme={props.colorScheme}
                            onUsefulButtonClicked={onUsefulButtonClicked}
                            onNotUsefulButtonClicked={onNotUsefulButtonClicked}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

function onFeedbackContainerClicked(evt: React.MouseEvent<any>) {
    // Suppress the onClick callback of feedback UX, otherwise it'll open the file.
    // This is needed because feedbackHoverTarget needs to be part of relatedFile, so that it shows up upon hovering over the file content,
    evt.stopPropagation();
}
